/**
 * Slow-operation classification for the RTK Query timing wrapper in `baseApi.ts`.
 *
 * Kept dependency-free (no `import.meta.env`, no Sentry import) so it can be unit
 * tested directly — mirrors the `chunkLoad.ts` pattern.
 *
 * The timing wrapper measures wall-clock time around every query. A long duration
 * only means "slow operation" when the request actually completed an HTTP
 * round-trip. `fetchBaseQuery` reports failures that never reached the server with
 * a non-numeric `status`:
 *   - "FETCH_ERROR"   network error / connection dropped / browser gave up
 *   - "TIMEOUT_ERROR" request exceeded the configured timeout
 * For these the measured time is just how long the request hung before it failed
 * (time-to-failure), not operation latency. Reporting them as "slow operations"
 * produces misleading alerts — and because `captureSlowOperation` escalates
 * anything >= 10s to `error` level, a network error that hangs ~22s gets reported
 * as a *critical* slow operation (Sentry ZENYT-PLATFORM-FRONTEND-STAGING-3).
 * The transport failure itself is returned to the caller for normal error
 * handling, so we exclude it from slow-op reporting here.
 */
export const TRANSPORT_FAILURE_STATUSES = new Set<string>(["FETCH_ERROR", "TIMEOUT_ERROR"]);
export const BEST_EFFORT_API_ENDPOINTS = new Set<string>(["trackEvent"]);

export interface TimedQueryResult {
  error?: unknown;
}

/** The status RTK Query exposes on a result: an HTTP code, an error tag, or "ok". */
export function getResultStatus(result: TimedQueryResult): number | string {
  const error = result?.error;
  if (error && typeof error === "object" && "status" in error) {
    const status = (error as { status: unknown }).status;
    if (typeof status === "number" || typeof status === "string") return status;
  }
  return error ? "error" : "ok";
}

/**
 * True when the request never completed a normal HTTP round-trip, so its duration
 * reflects time-to-failure rather than latency and must not be treated as "slow".
 */
export function isTransportFailure(result: TimedQueryResult): boolean {
  const status = getResultStatus(result);
  return typeof status === "string" && TRANSPORT_FAILURE_STATUSES.has(status);
}

/**
 * Best-effort endpoints should never become production error issues. They may be
 * useful to observe elsewhere, but their latency is intentionally non-blocking
 * for the user-facing flow that triggered them.
 */
export function isBestEffortApiEndpoint(endpoint?: string): boolean {
  return typeof endpoint === "string" && BEST_EFFORT_API_ENDPOINTS.has(endpoint);
}

/**
 * Whether a finished query should be reported as a slow operation: it exceeded the
 * threshold AND it actually completed a round-trip (success or HTTP error status).
 */
export function shouldReportSlowOperation(
  durationMs: number,
  thresholdMs: number,
  result: TimedQueryResult,
  endpoint?: string,
): boolean {
  return (
    durationMs >= thresholdMs &&
    !isTransportFailure(result) &&
    !isBestEffortApiEndpoint(endpoint)
  );
}
